import type { Currency, Lang } from "./constants";

// Prices are stored as integer minor units (bani / cents). Never use floating point for money.

export type PriceParseError = "empty" | "format" | "zero" | "big";

/** Highest accepted price (in major units), matching the approved prototype validation. */
export const MAX_PRICE_MAJOR = 9999;

export const PRICE_ERROR_MESSAGES: Record<PriceParseError, string> = {
  empty: "Introdu prețul.",
  format: "Folosește doar cifre și cel mult două zecimale, de ex. 56,00.",
  zero: "Prețul trebuie să fie mai mare decât 0.",
  big: "Verifică prețul: pare neobișnuit de mare.",
};

/** Parses user input such as "56", "56,5" or "56.50" into minor units without floating point. */
export function parsePriceInput(
  input: unknown,
): { ok: true; minor: number } | { ok: false; error: PriceParseError } {
  const text = String(input ?? "")
    .trim()
    .replace(/\s/g, "");
  if (!text) return { ok: false, error: "empty" };
  const m = /^(\d{1,6})(?:[.,](\d{1,2}))?$/.exec(text);
  if (!m) return { ok: false, error: "format" };
  const major = Number(m[1]);
  const fraction = Number((m[2] ?? "").padEnd(2, "0"));
  const minor = major * 100 + fraction;
  if (minor <= 0) return { ok: false, error: "zero" };
  if (minor > MAX_PRICE_MAJOR * 100) return { ok: false, error: "big" };
  return { ok: true, minor };
}

/** Number grouping of each menu language (the decimal mark is "." in English, "," in the others). */
const LOCALE: Record<Lang, string> = {
  ro: "ro-RO",
  en: "en-GB",
  de: "de-DE",
  fr: "fr-FR",
  it: "it-IT",
  es: "es-ES",
  hu: "hu-HU",
};

/** Formats minor units for display: "28,00 lei", "28,00 €", "RON 28.00", "€28.00", "28,00 RON". */
export function formatPrice(minor: number, currency: Currency, lang: Lang): string {
  const major = Math.trunc(minor / 100);
  const cents = String(Math.abs(minor % 100)).padStart(2, "0");
  const grouped = new Intl.NumberFormat(LOCALE[lang], { useGrouping: true }).format(major);
  const amount = `${grouped}${lang === "en" ? "." : ","}${cents}`;
  if (lang === "ro") return currency === "EUR" ? `${amount} €` : `${amount} lei`;
  if (lang === "en") return currency === "EUR" ? `€${amount}` : `RON ${amount}`;
  return currency === "EUR" ? `${amount} €` : `${amount} RON`;
}

/** Formats minor units as an editable input value, e.g. 5600 → "56,00". */
export function formatPriceInput(minor: number): string {
  return `${Math.trunc(minor / 100)},${String(minor % 100).padStart(2, "0")}`;
}
